'use client'
import { memo, useEffect, useId, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'

import { RaHeading, RaIcon, RaRichText, RaTypography } from '@components/core/index.core'
import { ICONS } from '@constants/icons.constants'

import styles from './ra-accordion.module.scss'

interface RaAccordionProps {
  title: string
  /** Replaces the default title, rendered beside a separate toggle button (e.g. a link). */
  header?: React.ReactNode
  content: string | React.ReactNode
  variant?: 'rich-content' | 'read-more' | 'mobile-nav' | 'mobile-nav-subitem'
  onOpen?: (value?: string) => void
  id?: string
}

export const RaAccordion = memo(
  ({ title, header, content, variant = 'rich-content', id, onOpen }: RaAccordionProps) => {
    const generatedId = useId()
    const bodyRef = useRef(null)
    const [open, setOpen] = useState(false)
    const [currentHeight, setCurrentHeight] = useState(0)

    const toggleOpen = () => {
      setOpen(!open)
    }

    const calculateContentHeight = () => {
      if (!bodyRef?.current) return null

      const observer = new ResizeObserver(entries => {
        window.requestAnimationFrame(() => {
          for (const entry of entries) {
            const cr = entry.contentRect
            let crHeight = cr?.height

            if (crHeight) crHeight += 12
            if (crHeight !== currentHeight) setCurrentHeight(crHeight)
          }
        })
      })

      for (const child of bodyRef.current.children) {
        const images = child?.querySelectorAll('img')
        if (images?.length > 0) {
          for (const img of images) {
            observer.observe(img)
          }
        }
        observer.observe(child)
      }

      return observer
    }

    useEffect(() => {
      const observer = calculateContentHeight()

      if (open && onOpen) {
        onOpen(title)
      }

      return () => {
        observer.disconnect()
      }
    }, [open])

    const accordionStyles = useMemo(() => {
      return clsx(
        styles['ra-accordion'],
        styles[`ra-accordion--${variant}`],
        open && styles['ra-accordion--open'],
      )
    }, [open, variant])

    const isMenuVariant = variant === 'mobile-nav' || variant === 'mobile-nav-subitem'

    const contentId = useMemo(() => id || `accordion-content-${generatedId}`, [id, generatedId])

    const toggleIcon = (
      <RaIcon
        icon={variant === 'read-more' ? ICONS.CHEVRON_DOWN : open ? ICONS.MINUS : ICONS.PLUS}
        color={isMenuVariant ? 'light' : 'dark'}
        aria-hidden="true"
      />
    )

    return (
      <div className={accordionStyles}>
        {header ? (
          <div className={styles['ra-accordion__header']}>
            {header}
            <button
              type="button"
              className={styles['ra-accordion__toggle']}
              onClick={toggleOpen}
              aria-expanded={open}
              aria-controls={contentId}
              aria-label={title}>
              {toggleIcon}
            </button>
          </div>
        ) : (
          <button
            type="button"
            className={styles['ra-accordion__header']}
            onClick={toggleOpen}
            aria-expanded={open}
            aria-controls={contentId}>
            {variant === 'read-more' || variant === 'mobile-nav-subitem' ? (
              <RaTypography
                component="span"
                weight={variant === 'read-more' ? 'semibold' : 'regular'}
                color={variant === 'read-more' ? 'dark' : 'light'}>
                {title}
              </RaTypography>
            ) : (
              <RaHeading
                component={variant === 'mobile-nav' ? 'h3' : 'h5'}
                color={isMenuVariant ? 'light' : 'primary'}
                noPadding>
                {title}
              </RaHeading>
            )}
            {toggleIcon}
          </button>
        )}
        <div
          ref={bodyRef}
          className={styles['ra-accordion__body']}
          role="region"
          id={contentId}
          aria-label={title}
          style={{
            height: open ? currentHeight : 0,
          }}
          {...(!open && { 'aria-hidden': 'true' })}>
          {typeof content === 'string' && <RaRichText content={content} />}
          {typeof content !== 'string' && content}
        </div>
      </div>
    )
  },
)

RaAccordion.displayName = 'RaAccordion'
